/**
 * udo-instant-price.js
 *
 * Makes the product price (and the Add-to-cart button price) update the instant
 * a customer clicks a variant option, instead of waiting for Horizon's
 * section-render fetch + morph (~300-500ms). It's an *optimistic* update: the
 * theme's own fetch still runs and reconciles everything (availability, media,
 * compare-at show/hide) a moment later, so a wrong guess here self-heals.
 *
 * Prices come from a Liquid-rendered JSON island (`script.udo-variant-price-map`
 * in add-to-cart-button.liquid), so the strings already match the shop's money
 * format exactly — no client-side money formatting, no currency guesswork.
 *
 * Scope: only the main product's variant-picker. Cards, quick-add dialogs and
 * the predictive-search dropdown are excluded so their prices are never touched.
 *
 * Also exposes `window.UdoInstantPrice.resolve(section)` — returns the matched
 * variant record ({ key, id, cents, available, price, compareAt }) for a
 * section's currently-checked options, or null. product-form.js uses it to set
 * the variant id and keep the Add-to-cart button active during a variant switch
 * instead of disabling it until the fetch resolves.
 */
(function () {
  'use strict';

  var EXCLUDE =
    'product-card, .product-card, quick-add-dialog, quick-add-component,' +
    ' predictive-search-component, .predictive-search-dropdown, .predictive-search-results';

  function parseMap(scriptEl) {
    try {
      var arr = JSON.parse(scriptEl.textContent);
      var map = Object.create(null);
      for (var i = 0; i < arr.length; i++) map[arr[i].key] = arr[i];
      return map;
    } catch (e) {
      return null;
    }
  }

  // Read the currently-checked option value from each option group, in DOM
  // order (which is product.options order), then join to match variant.title.
  // Handles both markups: 3.1 puts data-option-name on a wrapper div around
  // each option group; 4.x puts it on the radio inputs / select options
  // themselves.
  function checkedKey(picker) {
    var values = [];
    var i;

    // 3.1-style: wrapper elements carrying data-option-name.
    var wrappers = [];
    var all = picker.querySelectorAll('[data-option-name]');
    for (i = 0; i < all.length; i++) {
      var tag = all[i].tagName;
      if (tag !== 'INPUT' && tag !== 'OPTION' && tag !== 'SELECT') wrappers.push(all[i]);
    }
    if (wrappers.length) {
      for (i = 0; i < wrappers.length; i++) {
        var checked = wrappers[i].querySelector('input[type="radio"]:checked');
        if (checked) {
          values.push(checked.value);
          continue;
        }
        var select = wrappers[i].querySelector('select');
        if (select && select.value) values.push(select.value);
      }
      return values.length ? values.join(' / ') : null;
    }

    // 4.x-style: the attribute sits on the inputs/options — group by option
    // name in DOM order and take the checked/selected value of each group.
    var order = [];
    var byName = Object.create(null);
    var els = picker.querySelectorAll(
      'input[type="radio"][data-option-name], option[data-option-name], select[data-option-name]'
    );
    for (i = 0; i < els.length; i++) {
      var name = els[i].getAttribute('data-option-name');
      if (!(name in byName)) {
        byName[name] = null;
        order.push(name);
      }
      var v = null;
      if (els[i].tagName === 'INPUT' && els[i].checked) v = els[i].value;
      else if (els[i].tagName === 'OPTION' && els[i].selected) v = els[i].value;
      else if (els[i].tagName === 'SELECT' && els[i].value) v = els[i].value;
      if (v != null) byName[name] = v;
    }
    for (i = 0; i < order.length; i++) {
      if (byName[order[i]] == null) return null;
      values.push(byName[order[i]]);
    }
    return values.length ? values.join(' / ') : null;
  }

  // Resolve the variant record for a section's currently-checked options.
  // Returns { key, id, cents, available, price, compareAt } or null.
  function resolve(section) {
    if (!section) return null;
    var island = section.querySelector('script.udo-variant-price-map');
    if (!island) return null;
    var map = parseMap(island);
    if (!map) return null;
    var picker = section.querySelector('variant-picker');
    if (!picker || picker.closest(EXCLUDE)) return null;
    var key = checkedKey(picker);
    if (!key) return null;
    return map[key] || null;
  }

  function updateForPicker(picker) {
    if (!picker || picker.closest(EXCLUDE)) return; // main product only
    var section = picker.closest('.shopify-section');
    if (!section) return;
    var match = resolve(section);
    if (!match) return;

    // Displayed price(s) within this section (main + sticky bar), never in cards.
    var priceEls = section.querySelectorAll('.price');
    for (var i = 0; i < priceEls.length; i++) {
      if (priceEls[i].closest(EXCLUDE)) continue;
      priceEls[i].textContent = match.price;
    }

    // Add-to-cart button price.
    var atcEls = section.querySelectorAll('.add-to-cart-text__price');
    for (var j = 0; j < atcEls.length; j++) {
      atcEls[j].textContent = match.price;
      if (match.cents != null) atcEls[j].setAttribute('data-price', match.cents);
    }

    // Compare-at (best effort; the section morph reconciles show/hide shortly).
    if (match.compareAt) {
      var cmpEls = section.querySelectorAll('.compare-at-price');
      for (var k = 0; k < cmpEls.length; k++) {
        if (cmpEls[k].closest(EXCLUDE)) continue;
        cmpEls[k].textContent = match.compareAt;
      }
    }
  }

  function handle(event) {
    var t = event.target;
    if (!t || !t.closest) return;
    var picker = t.closest('variant-picker');
    if (picker) updateForPicker(picker);
  }

  // `change` covers radio/select inputs; `variant:selected` is Horizon's own
  // pre-fetch signal (dispatched on the picker, bubbles to document).
  document.addEventListener('change', handle, true);
  document.addEventListener('variant:selected', handle, true);

  // Shared resolver for product-form.js (keep-ATC-active-on-variant-switch).
  window.UdoInstantPrice = { resolve: resolve };
})();
